import styled from "styled-components";
import tw from "twin.macro";
import GradientButton from "../../../../components/GradientButton";

const Hero = styled.section`
  ${tw`relative py-6 md:py-8`}
  z-index: 1;
  min-height: 100vh;
  display: flex;
  align-items: center;

  @media (max-width: 767px) {
    min-height: auto;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("/novas-imagens/section-one/expiral.png");
    background-repeat: no-repeat;
    background-size: 760px auto;
    background-position: left -115px top -65px;
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 767px) {
    &::before {
      background-size: 620px auto;
      background-position: left -160px top -24px;
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Container = styled.div`
  ${tw`mx-auto`}
  position: relative;
  padding: var(--app-content-gutter);
  max-width: 1240px;
`;

const HeroTop = styled.div`
  ${tw`grid gap-6 items-center`}

  @media (min-width: 1024px) {
    grid-template-columns: 180px 630px 230px;
    justify-content: center;
    gap: 1.8rem;
  }
`;

const LogoWrap = styled.div`
  ${tw`flex justify-center`}

  @media (max-width: 767px) {
    justify-content: flex-start;
    min-height: 92px;
  }
`;

const Logo = styled.img`
  width: 176px;
  height: auto;
  display: block;

  @media (min-width: 1024px) {
    width: 130px;
  }

  @media (max-width: 767px) {
    width: 128px;
  }
`;

const TratiCardWrap = styled.div`
  ${tw`flex justify-center`}
  position: relative;
  width: 630px;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 0;
  border-radius: 22px;
  background: transparent;
  box-shadow: 0 12px 16px rgba(53, 53, 53, 0.22);

  @media (max-width: 767px) {
    width: min(100%, 620px);
  }
`;

const TratiCard = styled.iframe`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -12px;
  width: calc(100% + 24px);
  height: 100%;
  border: 0;
  display: block;
  background: transparent;
`;

const TratiPoster = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const TratiPlayButton = styled.button`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 74px;
  height: 52px;
  border: 0;
  border-radius: 14px;
  background: #ff0000;
  transform: translate(-50%, -50%);
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.24);
  transition: transform 160ms ease, filter 160ms ease;

  &::before {
    content: "";
    position: absolute;
    left: 30px;
    top: 15px;
    width: 0;
    height: 0;
    border-top: 11px solid transparent;
    border-bottom: 11px solid transparent;
    border-left: 17px solid #fff;
  }

  &:hover {
    filter: brightness(1.05);
    transform: translate(-50%, -50%) scale(1.03);
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.98);
  }
`;

const LinkStack = styled.div`
  ${tw`flex flex-col items-center lg:items-start`}
  gap: 1rem;

  @media (max-width: 767px) {
    display: none;
  }
`;

const BibleVerse = styled.div`
  position: absolute;
  top: clamp(1.5rem, 4vh, 3rem);
  right: var(--app-content-gutter);
  width: min(280px, 22vw);
  color: #437672;
  font-family: "Montserrat", sans-serif;
  text-align: right;
  z-index: 2;

  span,
  strong {
    display: block;
  }

  span {
    font-size: 12px;
    line-height: 1.35;
    font-weight: 500;
  }

  strong {
    margin-top: 0.15rem;
    font-size: 10px;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    display: none;
  }

  @media (max-width: 767px) {
    display: block;
    top: 2.7rem;
    right: var(--app-content-gutter);
    width: 178px;
    text-align: left;

    span {
      font-size: 11px;
      line-height: 1.3;
    }

    strong {
      font-size: 9px;
      letter-spacing: 0.06em;
    }
  }
`;

const NavDivider = styled.div`
  width: 220px;
  height: 1px;
  margin: 0.85rem 0 0.75rem;
  background: #cccccc;
  opacity: 0.85;
`;

const NavPill = styled.a`
  ${tw`inline-flex items-center justify-center text-center`}
  box-sizing: border-box;
  width: 100%;
  height: 24px;
  padding: 0 8px;
  border: 0;
  border-radius: 0;
  background: ${({ $active }) => ($active ? "#437672" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#437672")};
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.14);
  font: inherit;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
  appearance: none;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;

  &:hover {
    filter: brightness(1.03);
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 4px 7px rgba(0, 0, 0, 0.16);
  }
`;

const MobileLoginButton = styled.button`
  ${tw`hidden items-center justify-center text-center`}
  width: 184px;
  height: 48px;
  border: 0;
  background: #437672;
  color: #fff;
  box-shadow: 0 9px 12px rgba(0, 0, 0, 0.16);
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;

  &:hover {
    filter: brightness(1.03);
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 5px 8px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 767px) {
    display: inline-flex;
    justify-self: center;
    margin-top: 1.6rem;
  }
`;

const MobileLoginOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.36);

  @media (max-width: 767px) {
    display: flex;
  }
`;

const MobileLoginModal = styled.div`
  position: relative;
  width: min(420px, 100%);
  min-height: min(86vh, 680px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3.4rem;
  padding: 4rem 1.4rem 3.2rem;
  background: #fff;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.22);
`;

const MobileLoginClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
`;

const MobileLoginActions = styled.div`
  ${tw`flex flex-col items-center`}
  gap: 1.35rem;

  ${NavDivider} {
    width: min(320px, 72vw);
    margin: 1.25rem 0;
  }

  ${NavPill} {
    width: auto;
    min-width: 220px;
    height: 44px;
    padding: 0 28px;
    font-size: 14px;
    box-shadow: 0 9px 12px rgba(0, 0, 0, 0.14);
  }
`;

const MobileLoginLogo = styled.img`
  width: 186px;
  height: auto;
  display: block;
`;

const HeroBottom = styled.div`
  ${tw`flex flex-col items-center mt-10`}
  max-width: 1040px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 1024px) {
    margin-top: 3rem;
  }
`;

const CopyBlock = styled.div`
  ${tw`flex flex-col items-center justify-center text-center`}
  gap: 1rem;
  min-width: 0;
  padding-top: 0;

  @media (max-width: 767px) {
    display: contents;
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    gap: 1.5rem;
  }
`;

const Heading = styled.h1`
  margin: 0;
  font-size: 24px;
  line-height: 1;
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
  color: #437672;
  text-transform: uppercase;

  @media (max-width: 767px) {
    order: 1;
    max-width: 640px;
    font-size: 20px;
    line-height: 1.14;
  }

  @media (min-width: 1024px) {
    white-space: nowrap;
  }
`;

const Subheading = styled.p`
  margin: 1.35rem 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 400;
  letter-spacing: 0;
  color: #4d4b4b;
  text-align: center;
  text-transform: uppercase;

  strong {
    color: #4d4b4b;
    font-weight: 500;
  }

  @media (max-width: 767px) {
    order: 2;
    margin-top: 2.1rem;
    max-width: 640px;
    font-size: 16px;
    line-height: 1.35;
  }

  @media (min-width: 1024px) {
    white-space: nowrap;
  }
`;

const PrimaryCta = styled(GradientButton)`
  min-width: 220px;
  height: 40px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 767px) {
    order: 3;
    margin-top: 3.1rem;
    min-width: 0;
    width: min(440px, calc(100vw - 72px));
    height: 48px;
    font-size: 16px;
  }
`;

const FooterSpacer = styled.div`
  height: 8px;

  @media (min-width: 1024px) {
    height: 18px;
  }
`;

export {
  Hero,
  Container,
  HeroTop,
  LogoWrap,
  Logo,
  TratiCardWrap,
  TratiCard,
  TratiPoster,
  TratiPlayButton,
  LinkStack,
  BibleVerse,
  NavDivider,
  NavPill,
  MobileLoginButton,
  MobileLoginOverlay,
  MobileLoginModal,
  MobileLoginClose,
  MobileLoginActions,
  MobileLoginLogo,
  HeroBottom,
  CopyBlock,
  Heading,
  Subheading,
  PrimaryCta,
  FooterSpacer,
};
